import { useContext, useState, useCallback, useRef } from 'react';
import { FileUpload, ProgressIndicator, ToastContext, Card, CardContent, Button, UploadIcon } from '@forge/common';
import { filesApi } from '@/api/files';

interface UploadItem {
  id: string;
  fileName: string;
  fileSize: number;
  uploaded: number;
  status?: 'success' | 'error';
  statusLabel?: string;
}

interface FileUploadZoneProps {
  readonly onFilesUploaded: () => void;
}

const MAX_FILE_SIZE = 5 * 1024 * 1024 * 1024; // 5 GB

const formatSize = (bytes: number): string => {
  if (bytes >= 1073741824) return `${(bytes / 1073741824).toFixed(1)} GB`;
  if (bytes >= 1048576) return `${(bytes / 1048576).toFixed(1)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${bytes} B`;
};

const readFolderEntries = async (dirEntry: FileSystemDirectoryEntry): Promise<File[]> => {
  const reader = dirEntry.createReader();
  const files: File[] = [];

  const readBatch = (): Promise<FileSystemEntry[]> =>
    new Promise((resolve, reject) => reader.readEntries(resolve, reject));

  let entries = await readBatch();
  while (entries.length > 0) {
    for (const entry of entries) {
      if (entry.isFile) {
        const file = await new Promise<File>((resolve, reject) =>
          (entry as FileSystemFileEntry).file(resolve, reject),
        );
        files.push(file);
      } else if (entry.isDirectory) {
        const subFiles = await readFolderEntries(entry as FileSystemDirectoryEntry);
        files.push(...subFiles);
      }
    }
    entries = await readBatch();
  }
  return files;
};

const FileUploadZone = ({ onFilesUploaded }: FileUploadZoneProps) => {
  const [uploads, setUploads] = useState<UploadItem[]>([]);
  const toast = useContext(ToastContext);
  const folderInputRef = useRef<HTMLInputElement>(null);

  const uploadFileToApi = useCallback(
    async (file: File) => {
      const uploadId = `upload-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      const item: UploadItem = {
        id: uploadId,
        fileName: file.name,
        fileSize: file.size,
        uploaded: 0,
      };

      setUploads((prev) => [...prev, item]);

      try {
        // Show indeterminate progress
        setUploads((prev) =>
          prev.map((u) => (u.id === uploadId ? { ...u, uploaded: Math.floor(file.size * 0.5) } : u)),
        );

        await filesApi.uploadFile(file);

        setUploads((prev) =>
          prev.map((u) =>
            u.id === uploadId
              ? { ...u, uploaded: file.size, status: 'success', statusLabel: 'Complete' }
              : u,
          ),
        );
        toast.addSuccess({
          title: 'Upload Complete',
          content: `${file.name} (${formatSize(file.size)}) uploaded successfully`,
          lifetime: 4000,
        });
        onFilesUploaded();
        setTimeout(() => {
          setUploads((prev) => prev.filter((u) => u.id !== uploadId));
        }, 3000);
      } catch {
        setUploads((prev) =>
          prev.map((u) =>
            u.id === uploadId
              ? { ...u, status: 'error', statusLabel: 'Failed' }
              : u,
          ),
        );
        toast.addError({
          title: 'Upload Failed',
          content: `Failed to upload ${file.name}`,
          lifetime: 6000,
        });
        setTimeout(() => {
          setUploads((prev) => prev.filter((u) => u.id !== uploadId));
        }, 5000);
      }
    },
    [toast, onFilesUploaded],
  );

  const validateAndUpload = useCallback(
    (fileList: File[]) => {
      const validFiles: File[] = [];
      fileList.forEach((file) => {
        if (file.size > MAX_FILE_SIZE) {
          toast.addError({
            title: 'Upload Failed',
            content: `${file.name} exceeds the 5 GB file size limit`,
            lifetime: 6000,
          });
        } else if (file.size === 0) {
          // Skip empty files (common in folder uploads)
        } else {
          validFiles.push(file);
        }
      });

      validFiles.forEach((file) => uploadFileToApi(file));
      if (validFiles.length > 0) {
        // Upload triggered per file, parent reload happens on each success
      }
    },
    [toast, uploadFileToApi],
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const fileList = e.target.files;
      if (!fileList || fileList.length === 0) return;
      validateAndUpload(Array.from(fileList));
    },
    [validateAndUpload],
  );

  const handleDrop = useCallback(
    async (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      const items = e.dataTransfer.items;
      if (!items) return;

      const allFiles: File[] = [];
      const entries: FileSystemEntry[] = [];

      for (let i = 0; i < items.length; i++) {
        const entry = items[i].webkitGetAsEntry?.();
        if (entry) entries.push(entry);
      }

      for (const entry of entries) {
        if (entry.isFile) {
          const file = await new Promise<File>((resolve, reject) =>
            (entry as FileSystemFileEntry).file(resolve, reject),
          );
          allFiles.push(file);
        } else if (entry.isDirectory) {
          const folderFiles = await readFolderEntries(entry as FileSystemDirectoryEntry);
          allFiles.push(...folderFiles);
        }
      }

      if (allFiles.length > 0) {
        const folderCount = entries.filter((e) => e.isDirectory).length;
        if (folderCount > 0) {
          toast.addInfo({
            title: 'Folder Upload',
            content: `Found ${allFiles.length} file${allFiles.length > 1 ? 's' : ''} in ${folderCount} folder${folderCount > 1 ? 's' : ''}`,
            lifetime: 4000,
          });
        }
        validateAndUpload(allFiles);
      }
    },
    [toast, validateAndUpload],
  );

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleBrowseFolders = useCallback(() => {
    folderInputRef.current?.click();
  }, []);

  const handleFolderInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const fileList = e.target.files;
      if (!fileList || fileList.length === 0) return;
      const files = Array.from(fileList);
      toast.addInfo({
        title: 'Folder Upload',
        content: `Found ${files.length} file${files.length > 1 ? 's' : ''} in selected folder`,
        lifetime: 4000,
      });
      validateAndUpload(files);
    },
    [toast, validateAndUpload],
  );

  return (
    <div>
      <div onDrop={handleDrop} onDragOver={handleDragOver}>
        <FileUpload
          id="file-transfer-upload"
          variant="drag-drop"
          orientation="horizontal"
          description="Drag and drop files or folders here, or click to browse. Max 5 GB per file. Large files are uploaded in 4 MB chunks."
          multiple
          onChange={handleChange}
        />
      </div>

      <div className="mt-3">
        <input
          ref={folderInputRef}
          type="file"
          // @ts-expect-error webkitdirectory is non-standard but widely supported
          webkitdirectory=""
          directory=""
          multiple
          onChange={handleFolderInput}
          className="hidden"
        />
        <Button variant="tertiary" onClick={handleBrowseFolders}>
          <UploadIcon />
          <span className="ml-1">Browse Folders</span>
        </Button>
      </div>

      {uploads.length > 0 && (
        <Card className="mt-3">
          <CardContent>
            <p className="text-xs font-bold uppercase text-color-tertiary mb-2">
              Uploads ({uploads.length})
            </p>
            <div className="flex flex-col gap-2">
              {uploads.map((upload) => (
                <ProgressIndicator
                  key={upload.id}
                  fileName={upload.fileName}
                  fileSize={upload.fileSize}
                  uploaded={upload.uploaded}
                  status={upload.status}
                  statusLabel={upload.statusLabel}
                />
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default FileUploadZone;
